/**
 * useSettlementSession – settlement-specific session hook.
 *
 * Wraps the shared apiService to provide CRUD for settlement frame data,
 * read-only access to planner data, and settlement progress tracking.
 *
 * Key design:
 *  - Settlement frame data uses `estate_id` as the session_id so all
 *    executors for the same estate share the same data.
 *  - Planner data is read-only — executors can view but never modify.
 *  - Progress is persisted per-step so multiple executors see each other's work.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../utils/api';
import { trackSettlementStepComplete } from '../utils/analytics';

/* ── Types ── */

export interface SettlementFrameData {
  frameId: string;
  estateId: string;
  data: Record<string, unknown>;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SettlementStepProgress {
  stepId: string;
  phase: string;
  status: 'not-started' | 'in-progress' | 'complete';
  completedSubTasks: string[];
  completedChecklistItems: string[];
  notes?: string;
  documents?: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    storageKey?: string;
    uploadedAt: string;
    uploadedBy: string;
  }>;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UseSettlementSessionReturn {
  /** Current estate ID this session is scoped to */
  estateId: string | null;

  /** Whether we're loading data */
  isLoading: boolean;

  /** Last error message */
  error: string | null;

  /* ── Settlement frame data ── */
  /** Load settlement frame data for a given SFrame */
  loadFrameData: (frameId: string) => Promise<SettlementFrameData | null>;
  /** Save settlement frame data (shared across executors) */
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>;

  /* ── Read-only planner data ── */
  /** Load planner's estate planning data for a given planning frame (read-only) */
  loadPlannerData: (plannerFrameId: string) => Promise<Record<string, unknown> | null>;

  /* ── Step progress ── */
  /** Load progress for all steps in a phase */
  loadPhaseProgress: (phase: string) => Promise<SettlementStepProgress[]>;
  /** Update progress for a specific step */
  updateStepProgress: (stepId: string, phase: string, updates: Partial<SettlementStepProgress>) => Promise<boolean>;
  /** Mark a checklist item as complete or incomplete */
  toggleChecklistItem: (stepId: string, phase: string, itemId: string, completed: boolean) => Promise<boolean>;
  /** Mark a sub-task as complete or incomplete */
  toggleSubTask: (stepId: string, phase: string, subTaskId: string, completed: boolean) => Promise<boolean>;
}

/**
 * Settlement session hook.
 * @param estateId - The estate (planner) ID this settlement is for.
 * @param options.readOnly - When true, suppresses writes (archived estate).
 */
export function useSettlementSession(
  estateId: string | null,
  options?: { readOnly?: boolean }
): UseSettlementSessionReturn {
  const readOnly = options?.readOnly ?? false;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for frame data to reduce API calls
  const cache = useRef<Record<string, unknown>>({});

  /* ── Settlement frame data ── */

  const loadFrameData = useCallback(
    async (frameId: string): Promise<SettlementFrameData | null> => {
      if (!estateId) return null;
      const cacheKey = `frame:${estateId}:${frameId}`;
      if (cache.current[cacheKey]) return cache.current[cacheKey];

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.request(
          `/api/settlement/${estateId}/frame/${frameId}`,
          { method: 'GET' }
        );
        if (response.success && response.data) {
          const result: SettlementFrameData = {
            frameId,
            estateId,
            data: response.data.data || {},
            updatedAt: response.data.updated_at,
            updatedBy: response.data.updated_by,
          };
          cache.current[cacheKey] = result;
          return result;
        }
        return null;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to load frame data for ${frameId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load frame data');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [estateId]
  );

  const saveFrameData = useCallback(
    async (frameId: string, data: Record<string, unknown>): Promise<boolean> => {
      if (!estateId || readOnly) return false;

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.request(
          `/api/settlement/${estateId}/frame/${frameId}`,
          {
            method: 'POST',
            body: JSON.stringify({ data }),
          }
        );

        if (response.success) {
          // Invalidate cache
          const cacheKey = `frame:${estateId}:${frameId}`;
          delete cache.current[cacheKey];
          return true;
        }
        return false;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to save frame data for ${frameId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to save frame data');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [estateId, readOnly]
  );

  /* ── Planner data (read-only) ── */

  const loadPlannerData = useCallback(
    async (plannerFrameId: string): Promise<Record<string, unknown> | null> => {
      if (!estateId) return null;
      const cacheKey = `planner:${estateId}:${plannerFrameId}`;
      if (cache.current[cacheKey]) return cache.current[cacheKey];

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.request(
          `/api/settlement/${estateId}/planner-data/${plannerFrameId}`,
          { method: 'GET' }
        );
        if (response.success && response.data) {
          cache.current[cacheKey] = response.data;
          return response.data;
        }
        return null;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to load planner data for ${plannerFrameId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load planner data');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [estateId]
  );

  /* ── Step progress ── */

  const loadPhaseProgress = useCallback(
    async (phase: string): Promise<SettlementStepProgress[]> => {
      if (!estateId) return [];

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.request(
          `/api/settlement/${estateId}/progress/${phase}`,
          { method: 'GET' }
        );
        if (response.success && response.steps) {
          return response.steps;
        }
        return [];
      } catch (err) {
        console.error(`[useSettlementSession] Failed to load phase progress for ${phase}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load progress');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [estateId]
  );

  const updateStepProgress = useCallback(
    async (stepId: string, phase: string, updates: Partial<SettlementStepProgress>): Promise<boolean> => {
      if (!estateId || readOnly) return false;

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.request(
          `/api/settlement/${estateId}/progress/${phase}/${stepId}`,
          {
            method: 'PUT',
            body: JSON.stringify(updates),
          }
        );
        if (updates.status === 'complete') {
          trackSettlementStepComplete({ phase, stepId });
        }
        return !!response.success;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to update step progress:`, err);
        setError(err instanceof Error ? err.message : 'Failed to update progress');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [estateId, readOnly]
  );

  const toggleChecklistItem = useCallback(
    async (stepId: string, phase: string, itemId: string, completed: boolean): Promise<boolean> => {
      if (!estateId || readOnly) return false;

      try {
        const response = await apiService.request(
          `/api/settlement/${estateId}/progress/${phase}/${stepId}/checklist`,
          {
            method: 'PUT',
            body: JSON.stringify({ itemId, completed }),
          }
        );
        return !!response.success;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to toggle checklist item:`, err);
        setError(err instanceof Error ? err.message : 'Failed to update checklist');
        return false;
      }
    },
    [estateId, readOnly]
  );

  const toggleSubTask = useCallback(
    async (stepId: string, phase: string, subTaskId: string, completed: boolean): Promise<boolean> => {
      if (!estateId || readOnly) return false;

      try {
        const response = await apiService.request(
          `/api/settlement/${estateId}/progress/${phase}/${stepId}/subtask`,
          {
            method: 'PUT',
            body: JSON.stringify({ subTaskId, completed }),
          }
        );
        return !!response.success;
      } catch (err) {
        console.error(`[useSettlementSession] Failed to toggle sub-task:`, err);
        setError(err instanceof Error ? err.message : 'Failed to update sub-task');
        return false;
      }
    },
    [estateId, readOnly]
  );

  // Clear cache when estate changes
  useEffect(() => {
    cache.current = {};
  }, [estateId]);

  return {
    estateId,
    isLoading,
    error,
    loadFrameData,
    saveFrameData,
    loadPlannerData,
    loadPhaseProgress,
    updateStepProgress,
    toggleChecklistItem,
    toggleSubTask,
  };
}
